import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { isProcessingPaymentStatus } from '../constants/payment-methods';
import { useCustomerData } from '../contexts/customer-data-context';
import { usePaymentErrorContext } from '../contexts/payment-error-context';
import { usePaymentState } from '../contexts/payment-state-context';
import { PaymentError } from '../core/payment-error';
import { PaymentProcessor } from '../core/payment-processor';
import {
    getProviderFactory,
    PaymentProviderConfig
} from '../core/provider-registry';
import type { ProviderInitData } from '../core/provider-types';
import { isPollingAbortedError } from '../core/retry';
import {
    AvailablePaymentMethodsV1Dto,
    PaymentConfig,
    PaymentInstrumentDetailsV1Dto,
    PaymentMethod
} from '../types';
import { isRedirectBasedMethod } from '../utils/payment-methods';
import { parseProviderInitData } from '../utils/provider-data-parser';

export interface MandateDownloadData {
    mandateUrl: string;
    paymentRequestToken: string;
    paymentInstrumentDetails?: PaymentInstrumentDetailsV1Dto;
    paymentInstrumentToken?: string;
    fingerprint?: string;
}

interface ProviderState {
    status: 'idle' | 'initializing' | 'ready' | 'error';
    error: string | null;
    providerConfig: PaymentProviderConfig | null;
    paymentProvider: string | null;
}

type ProviderAction =
    | { type: 'START_INITIALIZATION' }
    | {
          type: 'INITIALIZATION_SUCCESS';
          providerConfig: PaymentProviderConfig;
          paymentProvider: string;
      }
    | { type: 'INITIALIZATION_ERROR'; error: string }
    | { type: 'RESET' };

const initialState: ProviderState = {
    status: 'idle',
    error: null,
    providerConfig: null,
    paymentProvider: null
};

const providerReducer = (
    state: ProviderState,
    action: ProviderAction
): ProviderState => {
    switch (action.type) {
        case 'START_INITIALIZATION':
            return {
                ...state,
                status: 'initializing',
                error: null
            };
        case 'INITIALIZATION_SUCCESS':
            return {
                status: 'ready',
                error: null,
                providerConfig: action.providerConfig,
                paymentProvider: action.paymentProvider
            };
        case 'INITIALIZATION_ERROR':
            return {
                status: 'error',
                error: action.error,
                providerConfig: null,
                paymentProvider: null
            };
        case 'RESET':
            return initialState;
        default:
            return state;
    }
};

interface UseProviderInitializationParams {
    methodType: PaymentMethod;
    paymentProcessor: PaymentProcessor;
    availablePaymentMethods?: AvailablePaymentMethodsV1Dto[];
    onPaymentSuccess?: () => void;
}

export const useProviderInitialization = ({
    methodType,
    paymentProcessor,
    availablePaymentMethods,
    onPaymentSuccess
}: UseProviderInitializationParams) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [state, dispatch] = useReducer(providerReducer, initialState);
    const { addError, reportError, clearErrors, hasExternalErrorHandler } =
        usePaymentErrorContext();
    const { registerSubmitHandler, setProcessing, setCanSubmit } =
        usePaymentState();
    const { customerData } = useCustomerData();

    const originalConfigRef = useRef<PaymentConfig | null>(null);
    const currentProviderConfigRef = useRef<PaymentProviderConfig | null>(null);
    const providerConfigForSubmitRef = useRef<PaymentProviderConfig | null>(
        null
    );
    const requireSignatureRef = useRef(false);

    const [isFormValid, setIsFormValid] = useState(false);
    const [paymentStopReason, setPaymentStopReason] = useState<
        'canceled' | 'failed' | null
    >(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const isRetryingRef = useRef(false);
    const isReinitializingRef = useRef(false);
    const [mandateDownloadData, setMandateDownloadData] =
        useState<MandateDownloadData | null>(null);

    const cleanupProvider = useCallback(() => {
        if (currentProviderConfigRef.current) {
            try {
                currentProviderConfigRef.current.destroy();
            } catch (error) {
                console.warn(
                    '[PaymentWidget] Failed to destroy provider:',
                    error
                );
            }
            currentProviderConfigRef.current = null;
        }

        providerConfigForSubmitRef.current = null;

        if (originalConfigRef.current) {
            try {
                paymentProcessor.configure(originalConfigRef.current);
            } catch (error) {
                console.warn(
                    '[PaymentWidget] Failed to restore config:',
                    error
                );
            }
            originalConfigRef.current = null;
        }
    }, [paymentProcessor]);

    const fetchAvailablePaymentMethods = useCallback(async (): Promise<
        AvailablePaymentMethodsV1Dto[]
    > => {
        if (availablePaymentMethods?.length) {
            return availablePaymentMethods;
        }
        return paymentProcessor.getAvailablePaymentMethods();
    }, [availablePaymentMethods, paymentProcessor]);

    const findProviderForMethod = useCallback(
        (
            methods: AvailablePaymentMethodsV1Dto[]
        ): AvailablePaymentMethodsV1Dto => {
            const provider = methods.find((p) =>
                p.paymentMethods?.some(
                    (method) => method.paymentMethod === methodType
                )
            );

            if (!provider) {
                throw new PaymentError(
                    `No provider found for payment method: ${methodType}`,
                    'VALIDATION_ERROR'
                );
            }

            return provider;
        },
        [methodType]
    );

    const prepareProviderInitData = useCallback(
        async (
            provider: AvailablePaymentMethodsV1Dto,
            providerConfig: PaymentProviderConfig
        ) => {
            let baseInitData: ProviderInitData | undefined;
            try {
                baseInitData = parseProviderInitData(provider);
            } catch (error) {
                addError(error);
                baseInitData = undefined;
            }

            if (providerConfig.prepareForInitialization) {
                return providerConfig.prepareForInitialization(
                    paymentProcessor,
                    methodType,
                    baseInitData
                );
            }

            return baseInitData;
        },
        [paymentProcessor, methodType, addError]
    );

    const createPaymentConfig = useCallback(
        (baseConfig: PaymentConfig): PaymentConfig => {
            if (!baseConfig) {
                throw new PaymentError(
                    'PaymentProcessor not configured',
                    'VALIDATION_ERROR'
                );
            }

            if (!originalConfigRef.current) {
                originalConfigRef.current = { ...baseConfig };
            }

            const enableMandateDownload =
                baseConfig.featureFlags?.enableMandateDownload ?? true;

            return {
                ...baseConfig,
                container: containerRef.current!,
                onSuccess: (
                    paymentRequestToken: string,
                    paymentInstrumentDetails?: PaymentInstrumentDetailsV1Dto,
                    paymentInstrumentToken?: string,
                    fingerprint?: string
                ) => {
                    const mandateUrl =
                        paymentInstrumentDetails?.ach?.mandateUrl ??
                        paymentInstrumentDetails?.becs?.mandateUrl;

                    if (
                        (methodType === 'ACH' || methodType === 'BECS') &&
                        enableMandateDownload &&
                        mandateUrl
                    ) {
                        setMandateDownloadData({
                            mandateUrl,
                            paymentRequestToken,
                            paymentInstrumentDetails,
                            paymentInstrumentToken,
                            fingerprint
                        });
                        return;
                    }

                    originalConfigRef.current?.onSuccess?.(
                        paymentRequestToken,
                        paymentInstrumentDetails,
                        paymentInstrumentToken,
                        fingerprint
                    );
                    onPaymentSuccess?.();
                }
            };
        },
        [onPaymentSuccess, methodType]
    );

    const reinitializeProvider = useCallback(async () => {
        if (isReinitializingRef.current) {
            return;
        }

        isReinitializingRef.current = true;

        try {
            cleanupProvider();
            paymentProcessor.reset();
            await paymentProcessor.createPaymentRequest();
            dispatch({ type: 'RESET' });
        } catch (error) {
            const paymentError = PaymentError.fromUnknown(
                error,
                'API_ERROR',
                'Failed to create payment request'
            );
            dispatch({
                type: 'INITIALIZATION_ERROR',
                error: paymentError.message
            });
            addError(paymentError);
        } finally {
            isReinitializingRef.current = false;
        }
    }, [addError, cleanupProvider, paymentProcessor]);

    const setupErrorHandler = useCallback(
        (providerConfig: PaymentProviderConfig, providerType: string) => {
            if (!providerConfig.setErrorHandler) {
                return;
            }

            providerConfig.setErrorHandler((error: unknown) => {
                addError(error);

                if (providerType !== 'ADYEN') {
                    return;
                }

                const status = paymentProcessor.getCurrentStatus();
                if (status && isProcessingPaymentStatus(status)) {
                    return;
                }

                void reinitializeProvider();
            });
        },
        [addError, reinitializeProvider, paymentProcessor]
    );

    const setupPspErrorHandler = useCallback(
        (providerConfig: PaymentProviderConfig) => {
            providerConfig.setPspErrorHandler?.((error: unknown) => {
                if (hasExternalErrorHandler || methodType === 'BACS') {
                    addError(error);
                }
            });
        },
        [addError, hasExternalErrorHandler, methodType]
    );

    const setupClearErrorsHandler = useCallback(
        (providerConfig: PaymentProviderConfig) => {
            providerConfig.setClearErrorsHandler?.(clearErrors);
        },
        [clearErrors]
    );

    const setupCancellationHandler = useCallback(
        (providerConfig: PaymentProviderConfig) => {
            providerConfig.setCancellationHandler?.((error) => {
                setProcessing(false);
                setCanSubmit(false);

                if (error) {
                    reportError(error);
                }

                setPaymentStopReason(error ? 'failed' : 'canceled');
            });
        },
        [reportError, setCanSubmit, setProcessing]
    );

    const setupProcessingStateHandler = useCallback(
        (providerConfig: PaymentProviderConfig) => {
            if (!providerConfig.setProcessingState) {
                return;
            }

            providerConfig.setProcessingState((isProcessing: boolean) => {
                setProcessing(isProcessing);
                if (isProcessing) {
                    setCanSubmit(false);
                }
            });
        },
        [setProcessing, setCanSubmit]
    );

    const setupValidationHandler = useCallback(
        (providerConfig: PaymentProviderConfig) => {
            if (!providerConfig.setValidationChangeHandler) {
                return;
            }

            providerConfig.setValidationChangeHandler((isValid: boolean) => {
                setIsFormValid(isValid);
                const config = paymentProcessor.getConfig();
                if (
                    config?.hidePaymentButton &&
                    !isRedirectBasedMethod(methodType)
                ) {
                    setCanSubmit(isValid);
                }
            });
        },
        [paymentProcessor, setCanSubmit, methodType]
    );

    const initializeProvider = useCallback(async () => {
        if (!containerRef.current || state.status === 'initializing') {
            return;
        }

        dispatch({ type: 'START_INITIALIZATION' });

        try {
            const methods = await fetchAvailablePaymentMethods();

            const provider = findProviderForMethod(methods);

            const providerFactory = getProviderFactory(
                provider.paymentProvider
            );
            const providerConfig = await providerFactory(paymentProcessor);

            setupErrorHandler(providerConfig, provider.paymentProvider ?? '');
            setupPspErrorHandler(providerConfig);
            setupClearErrorsHandler(providerConfig);
            setupCancellationHandler(providerConfig);
            setupProcessingStateHandler(providerConfig);
            setupValidationHandler(providerConfig);

            const paymentMethodConfig = provider.paymentMethods?.find(
                (m) => m.paymentMethod === methodType
            );
            const requireSignature =
                paymentMethodConfig?.requireDirectDebitSignature ?? false;
            requireSignatureRef.current = requireSignature;

            if (requireSignature && providerConfig.setRequireSignature) {
                providerConfig.setRequireSignature(requireSignature);
            }

            const providerInitData = await prepareProviderInitData(
                provider,
                providerConfig
            );

            const baseConfig = paymentProcessor.getConfig();
            const pspConfig = createPaymentConfig(baseConfig);
            paymentProcessor.configure(pspConfig);

            await providerConfig.initialize(
                pspConfig,
                methodType,
                providerInitData,
                customerData || undefined
            );

            currentProviderConfigRef.current = providerConfig;
            dispatch({
                type: 'INITIALIZATION_SUCCESS',
                providerConfig,
                paymentProvider: provider.paymentProvider ?? ''
            });
        } catch (error) {
            if (isPollingAbortedError(error)) {
                return;
            }

            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to initialize payment';
            dispatch({ type: 'INITIALIZATION_ERROR', error: errorMessage });

            void addError(error);
        }
    }, [
        fetchAvailablePaymentMethods,
        findProviderForMethod,
        setupErrorHandler,
        setupPspErrorHandler,
        setupClearErrorsHandler,
        setupCancellationHandler,
        setupProcessingStateHandler,
        setupValidationHandler,
        prepareProviderInitData,
        createPaymentConfig,
        paymentProcessor,
        methodType,
        addError,
        state.status,
        customerData
    ]);

    useEffect(() => {
        return () => {
            setProcessing(false);
            setCanSubmit(false);
            cleanupProvider();
        };
    }, [cleanupProvider, setProcessing, setCanSubmit]);

    useEffect(() => {
        if (containerRef.current && state.status === 'idle') {
            void initializeProvider();
        }
    }, [initializeProvider, state.status]);

    useEffect(() => {
        if (
            containerRef.current &&
            state.status === 'ready' &&
            state.providerConfig?.mount
        ) {
            state.providerConfig.mount(containerRef.current, methodType);
        }

        return () => {
            if (state.providerConfig?.unmount) {
                state.providerConfig.unmount();
            }
        };
    }, [state.status, state.providerConfig, methodType]);

    useEffect(() => {
        providerConfigForSubmitRef.current = state.providerConfig;
    }, [state.providerConfig]);

    useEffect(() => {
        const config = paymentProcessor.getConfig();

        if (
            state.status === 'ready' &&
            state.providerConfig?.submit &&
            config?.hidePaymentButton
        ) {
            if (isRedirectBasedMethod(methodType)) {
                setCanSubmit(false);
                return;
            }

            registerSubmitHandler(async () => {
                if (providerConfigForSubmitRef.current?.submit) {
                    await providerConfigForSubmitRef.current.submit();
                }
            });

            if (!state.providerConfig.setValidationChangeHandler) {
                setCanSubmit(true);
            }
        }
    }, [
        state.status,
        state.providerConfig,
        paymentProcessor,
        registerSubmitHandler,
        setCanSubmit,
        methodType
    ]);

    useEffect(() => {
        if (
            state.status === 'ready' &&
            state.providerConfig?.updateCustomerFields &&
            customerData
        ) {
            state.providerConfig.updateCustomerFields(customerData);
        }
    }, [customerData, state.status, state.providerConfig]);

    const retryPayment = useCallback(async () => {
        if (isRetryingRef.current) {
            return;
        }

        isRetryingRef.current = true;
        setIsRetrying(true);
        setProcessing(true);
        setCanSubmit(false);
        clearErrors();
        cleanupProvider();
        paymentProcessor.clearPaymentRequestToken();

        try {
            await paymentProcessor.createPaymentRequest();
            setPaymentStopReason(null);
            dispatch({ type: 'RESET' });
        } catch (error) {
            const paymentError = PaymentError.fromUnknown(
                error,
                'API_ERROR',
                'Failed to create payment request'
            );
            dispatch({
                type: 'INITIALIZATION_ERROR',
                error: paymentError.message
            });
            addError(paymentError);
        } finally {
            setProcessing(false);
            setIsRetrying(false);
            isRetryingRef.current = false;
        }
    }, [
        addError,
        cleanupProvider,
        clearErrors,
        paymentProcessor,
        setCanSubmit,
        setProcessing
    ]);

    const setBacsConsent = useCallback(
        (consent: boolean) => {
            if (
                state.status === 'ready' &&
                state.providerConfig?.setBacsConsent &&
                methodType === 'BACS'
            ) {
                state.providerConfig.setBacsConsent(consent);
            }
        },
        [state.status, state.providerConfig, methodType]
    );

    const setSignature = useCallback(
        (dataUrl: string | undefined) => {
            if (
                state.status === 'ready' &&
                state.providerConfig?.setSignature
            ) {
                state.providerConfig.setSignature(dataUrl);
            }
        },
        [state.status, state.providerConfig]
    );

    const completeMandateDownload = useCallback(() => {
        if (!mandateDownloadData) {
            return;
        }

        originalConfigRef.current?.onSuccess?.(
            mandateDownloadData.paymentRequestToken,
            mandateDownloadData.paymentInstrumentDetails,
            mandateDownloadData.paymentInstrumentToken,
            mandateDownloadData.fingerprint
        );
        onPaymentSuccess?.();
        setMandateDownloadData(null);
    }, [mandateDownloadData, onPaymentSuccess]);

    return {
        containerRef,
        isInitializing:
            state.status === 'idle' || state.status === 'initializing',
        isInitialized: state.status === 'ready',
        isFormValid,
        requireSignature: requireSignatureRef.current,
        setBacsConsent,
        setSignature,
        providerConfig: state.providerConfig,
        paymentProvider: state.paymentProvider,
        mandateDownloadData,
        completeMandateDownload,
        paymentStopReason,
        isRetrying,
        retryPayment
    };
};
